import { Badge } from "@/components/ui/badge";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAssets } from "@/hooks/use-assets";
import { Calendar } from "lucide-react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router";

export function NavVersions() {
  const assetsQuery = useAssets();
  const assets = useMemo(() => assetsQuery.data?.assets ?? [], [assetsQuery.data?.assets]);
  const isLoading = assetsQuery.isLoading;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const hasVersionParam = searchParams.has("version");
  const [isExpanded, setIsExpanded] = useState(false);

  // Memoize the version grouping computation
  const { sortedVersions, versionCounts } = useMemo(() => {
    // Group assets by year.month (YYYY.MM) from display_release_month
    const groups = (assets || []).reduce(
      (acc, asset) => {
        // Use the pre-computed display_release_month field from backend
        const releaseYearMonth = asset.display_release_month || "Unknown";

        return {
          ...acc,
          [releaseYearMonth]: [...(acc[releaseYearMonth] || []), asset],
        };
      },
      {} as Record<string, typeof assets>,
    );

    // Calculate unique asset name counts for each version
    const counts = Object.entries(groups).reduce(
      (acc, [version, assetList]) => {
        const uniqueNames = new Set(assetList.map((asset) => asset.asset_name));
        return {
          ...acc,
          [version]: uniqueNames.size,
        };
      },
      {} as Record<string, number>,
    );

    // Sort versions in descending order (newest first)
    const sorted = Object.keys(groups).sort((a, b) => {
      // Handle "Unknown" version
      if (a === "Unknown") return 1;
      if (b === "Unknown") return -1;
      // Compare as version strings (YYYY.MM format sorts correctly)
      return b.localeCompare(a);
    });

    return { versionGroups: groups, sortedVersions: sorted, versionCounts: counts };
  }, [assets]);

  // Show only first 3 versions when collapsed
  const displayedVersions = isExpanded ? sortedVersions : sortedVersions.slice(0, 3);
  const hasMoreVersions = sortedVersions.length > 3;
  const hasVersions = sortedVersions.length > 0;
  const hasAssets = assets && assets.length > 0;

  return (
    <TooltipProvider>
      <SidebarGroup>
        <SidebarGroupContent className="flex flex-col gap-2">
          <SidebarMenu>
            {/* Main Release Link */}
            <NavLink to="/versions" end>
              {({ isActive }) => (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip="Browse by Release"
                    isActive={isActive || hasVersionParam}
                    disabled={isLoading || !hasAssets}
                  >
                    <Calendar />
                    <span>Release</span>
                    {hasMoreVersions && !isLoading && (
                      <Badge variant="secondary" className="ml-auto">
                        {sortedVersions.length}
                      </Badge>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </NavLink>

            {/* Loading State */}
            {isLoading && (
              <div className="ml-4 mt-1 space-y-2">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !hasVersions && (
              <div className="ml-4 mt-1 px-2 py-3 text-sm text-muted-foreground">No releases available</div>
            )}

            {/* Release List - Only show when not loading and has versions */}
            {!isLoading && hasVersions && (
              <div className="ml-4 mt-1 space-y-1">
                <div
                  className={`scrollbar-visible overflow-y-auto ${isExpanded ? "max-h-[300px]" : "max-h-none"}`}
                  style={{
                    scrollbarWidth: "thin",
                    scrollbarColor: "rgb(100 116 139) transparent",
                  }}
                >
                  <SidebarMenuSub>
                    {displayedVersions.map((version) => {
                      const currentVersion = searchParams.get("version");
                      const isVersionActive = location.pathname === "/versions" && currentVersion === version;

                      return (
                        <NavLink
                          key={version}
                          to={`/versions?version=${encodeURIComponent(version)}`}
                          className="block"
                        >
                          {() => (
                            <SidebarMenuSubItem>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <SidebarMenuSubButton
                                    isActive={isVersionActive}
                                    className={`w-full justify-between ${
                                      isVersionActive ? "bg-accent/80 font-semibold hover:bg-accent" : ""
                                    }`}
                                  >
                                    <span className="font-mono text-xs">{version}</span>
                                    <Badge variant="outline" className="ml-auto">
                                      {versionCounts[version]}
                                    </Badge>
                                  </SidebarMenuSubButton>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  <p>Release {version}</p>
                                </TooltipContent>
                              </Tooltip>
                            </SidebarMenuSubItem>
                          )}
                        </NavLink>
                      );
                    })}
                  </SidebarMenuSub>
                </div>

                {/* Expand/Collapse button - only show if there are more than 3 versions */}
                {hasMoreVersions && (
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="flex w-full items-center justify-center gap-1 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUp className="h-3 w-3" />
                        <span>Show less</span>
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3" />
                        <span>Show {sortedVersions.length - 3} more</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </TooltipProvider>
  );
}
